import Vue from "vue";
import posthog from 'posthog-js';
import { Component } from "vue-property-decorator";
import { INavbar } from "../../../../models/app-configuration";
import { Mutation, namespace, Getter, Action } from "vuex-class";
import { storeModules, storeTypes } from "../../../../store";
import { IAuthConfigModel } from "../../../../models/user/auth-config.model";
import EventTracker from "../../../../utils/event-tracker";
import { TRACKING_EVENTS } from "../../../../common/tracking-event.constants";
import EventBus from "../../../../utils/event-bus";
import { LOCALSTORAGE_AUTH2 } from "../../../../common/constants";
import { MODULE_ICON_URLS, NAVBAR_ICON_KEY_BY_NAME, NAVBAR_NORMAL_ICON_URLS } from "../../../../common/uri/module-icons.constants";
import { ConfigurationServices } from "../../../../services/configuration.service";

const CommonGetter = namespace(storeModules.Common, Getter);
const CommonMutation = namespace(storeModules.Common, Mutation);
const CommonAction = namespace(storeModules.Common, Action);
const UserGetter = namespace(storeModules.User, Getter);

// Add this to the navbars.json file after testing
const EMAIL_WEBSITE_INFO: any =   {
    "id": 20,
    "path": "/emails-website",
    "NameEN": "Emails (beta)",
    "NameNL": "Emails (beta)",
    "NameFR": "Emails (beta)",
    "icon": null,
    "index": 20,
    "parentId": 0,
    "navtiveId": 20,
    "isDefault": false,
    "name": "emails"
}

const KANBAN_INFO: any = {
    "id": 21,
    "path": "/kanban-website",
    "NameEN": "Kanban (beta)",
    "NameNL": "Kanban (beta)",
    "NameFR": "Kanban (beta)",
    "icon": null,
    "index": 21,
    "parentId": 0,
    "navtiveId": 21,
    "isDefault": false,
    "name": "kanban"
}

const ENQUIRY_CENTRE_INFO: any = {
    "id": 23,
    "path": "/enquiry-centre",
    "NameEN": "Enquiry centre (beta)",
    "NameNL": "Enquiry centre (beta)",
    "NameFR": "Enquiry centre (beta)",
    "icon": null,
    "index": 23,
    "parentId": 0,
    "navtiveId": 23,
    "isDefault": false,
    "name": "enquiry-centre"
}

const DASHBOARD_INFO: any =   {
	"id": 10,
	"path": "/dashboard",
	"NameEN": "Dashboard",
	"NameNL": "Dashboard",
	"NameFR": "Dashboard",
	"icon": "icon-size-20 ico_dashboard",
	"index": 10,
	"parentId": 0,
	"navtiveId": 10,
	"isDefault": false,
	"name": "dashboard"
}

const MARKETING_INFO: any = {
	"id": 30,
	"path": "/marketing-dashboard",
	"NameEN": "Marketing",
	"NameNL": "Marketing",
	"NameFR": "Marketing",
	"icon": null,
	"index": 30,
	"parentId": 0,
	"navtiveId": 30,
	"isDefault": false,
	"name": "marketing"
}

@Component({
	template: require("./template.html")
})
class NavbarComponent extends Vue {
	@CommonGetter
	navbar: INavbar;
	@CommonMutation
	setPageActive: (navbar: INavbar) => void;
	@CommonAction(storeTypes.Set.HORIZONTALPAGE)
	storeSetHorizontalPage: (isHorizontalPage: boolean) => boolean;

	@UserGetter('authConfig') private authConfig: IAuthConfigModel;
	@UserGetter userRights: any;

	dtNavbars: INavbar[];
	dtCurrentNavbar: INavbar | null;
	isCollapse: boolean = true;
    isShow: boolean = false;
    isDisplayButton: boolean = true;
    configServices: any = new ConfigurationServices();

	get coRouteName(): string {
		return this.$route.name;
	}
	get coCurrentNavbar(): INavbar {
		return this.navbar;
	}

	get dashboardModuleVisible() {
		return this.authConfig && this.authConfig.Config && this.authConfig.Config.ShowDashboardModule
	}

	// Mirrors the WinApp behaviour: the Configuration module needs the
	// "General configuration" user right (master users bypass).
	get configurationModuleVisible() {
		if (this.authConfig?.IsMasterUser) {
			return true;
		}
		return (this as any).$checkUserRights('General', 9);
	}

	isNavbarItemVisible(navbar: INavbar): boolean {
		if (navbar.name === 'dashboard') {
			return this.dashboardModuleVisible;
		}
		if (navbar.name === 'configuration') {
			return this.configurationModuleVisible;
		}
		return true;
	}

	get activeNavPath(): string {
		const match = this.dtNavbars.find(nav => this.isNavbarSelected(nav));
		return match ? match.path : '';
	}

	getNavbarIcons(navbar: INavbar): { NORMAL: string; ACTIVE: string } | null {
		const key = NAVBAR_ICON_KEY_BY_NAME[navbar.name];
		if (!key) return null;
		const icons = MODULE_ICON_URLS[key];
		return { NORMAL: NAVBAR_NORMAL_ICON_URLS[key] ?? icons.NORMAL, ACTIVE: icons.ACTIVE };
	}

	isNavbarSelected(navbar: INavbar): boolean {
		const path = this.$route?.path;
		if (!path || !navbar.path) return false;
		return path === navbar.path || path.startsWith(navbar.path + '/');
	}

	get isSelectedEnquiryCentre() {
		return this.$route?.path == '/enquiry-centre'
	}

	get customerName() {
		const authInfo = JSON.parse(localStorage.getItem(LOCALSTORAGE_AUTH2) || '{}');
		return authInfo?.data?.information?.customerName;
	}

	constructor() {
		super();
		this.dtNavbars = require("../../../../json/navbars.json") as INavbar[];
	}

	menuChange(m) {
        this.$router.push({ path: m.index });
        EventTracker.logEvent({
            FormName: TRACKING_EVENTS.PAGES.GLOBAL,
            Action: TRACKING_EVENTS.EVENTS.SELECT_MAIN_MENU,
            FormId: 0,
            Comment: {path: m.index}
        })
	}

	handleColapseMainMenu() {
		this.isCollapse = !this.isCollapse;
	}

	handleOpenMenu() {
		this.isShow = !this.isShow;
	}

	handleCloseMenu() {
		if (this.isShow) this.isShow = false;
    }

    async mounted() {
		// Handle collapse filter event
		EventBus.$on('COLLAPSE_FILTER', (val) => {
			this.isDisplayButton = !val;
		});
		
		// Handle sign document display status
		try {
			const systemConfigKeys = ['AllowEsignWithEidEasy'];
            const res = await this.configServices.getConfigValue(systemConfigKeys);
			if (!res?.SystemConfigs?.AllowEsignWithEidEasy) {
				this.dtNavbars = this.dtNavbars.filter(x => x.id !== 9);
			}
		} catch (error) {
			console.error('Error getting sign document display status:', error);
		}
		
		await this.$nextTick();
	
		// Handle Kanban feature flag - non-blocking approach
		this.checkPosthogFeatureFlag();
	}
    
    private isDestroyed = false;

    beforeDestroy() {
        this.isDestroyed = true;
        EventBus.$off('COLLAPSE_FILTER');
    }

	async checkPosthogFeatureFlag() {
		// Add whichever flags are ready right now so tabs aren't delayed waiting for
		// the slowest one. The retry loop only catches flags that arrive late.
		this.addAvailableFlaggedNavbarItems();

		// Robustly add items whenever PostHog (re)loads flags — covers the case where
		// flags arrive after the retry window (e.g. on a plain refresh). The
		// onFeatureFlags listener can't be unregistered, so guard against firing on a
		// torn-down instance after the navbar is destroyed.
		if (typeof posthog.onFeatureFlags === "function") {
			posthog.onFeatureFlags(() => {
				if (this.isDestroyed) return;
				this.addAvailableFlaggedNavbarItems();
			});
		}

		if (this.areAllFlaggedNavbarItemsAdded()) {
			return;
		}

		let retryCount = 0;
		const maxRetries = 5;
		const retryInterval = 1000;

		const checkInterval = setInterval(() => {
			retryCount++;
			this.addAvailableFlaggedNavbarItems();
			if (this.areAllFlaggedNavbarItemsAdded() || retryCount >= maxRetries) {
				clearInterval(checkInterval);
			}
		}, retryInterval);
	}

	addAvailableFlaggedNavbarItems() {
		if (this.isDashboardFlagEnabled()) {
			this.addDashboardToNavigation();
		}
		if (this.isMarketingFlagEnabled()) {
			this.addMarketingToNavigation();
		}
		if (this.isKanbanFlagEnabled()) {
			this.addKanbanToNavigation();
		}
		if (this.isEmailFlagEnabled()) {
			this.addEmailToNavigation();
		}
		if (this.isEnquiryCentreFlagEnabled()) {
			this.addEnquiryCentreToNavigation();
		}
	}

	areAllFlaggedNavbarItemsAdded() {
		return this.isKanbanFlagEnabled()
			&& this.isEmailFlagEnabled()
			&& this.isDashboardFlagEnabled()
			&& this.isMarketingFlagEnabled()
			&& this.isEnquiryCentreFlagEnabled();
	}

	addKanbanToNavigation() {
		// Check if already added to prevent duplicates
		if (!this.dtNavbars.find(nav => nav.id === KANBAN_INFO.id)) {
			this.dtNavbars.push(KANBAN_INFO);
		}
	}

	addEmailToNavigation() {
		if (!this.dtNavbars.find(nav => nav.id === EMAIL_WEBSITE_INFO.id)) {
			this.dtNavbars.push(EMAIL_WEBSITE_INFO);
		}
	}

	addDashboardToNavigation() {
		if (!this.dtNavbars.find(nav => nav.id === DASHBOARD_INFO.id)) {
			this.dtNavbars.push(DASHBOARD_INFO);
		}
	}

	addMarketingToNavigation() {
		if (!this.dtNavbars.find(nav => nav.id === MARKETING_INFO.id)) {
			this.dtNavbars.push(MARKETING_INFO);
		}
	}

	addEnquiryCentreToNavigation() {
		if (!this.dtNavbars.find(nav => nav.id === ENQUIRY_CENTRE_INFO.id)) {
			this.dtNavbars.push(ENQUIRY_CENTRE_INFO);
		}
	}

	isKanbanFlagEnabled() {
		return posthog.isFeatureEnabled('kanban-board');
	}

	isEmailFlagEnabled() {
		return posthog.isFeatureEnabled('automation-editor');
	}

	isDashboardFlagEnabled() {
		return posthog.isFeatureEnabled('dashboard-visible-flag');
	}

	isMarketingFlagEnabled() {
		return posthog.isFeatureEnabled('marketing-dashboard');
	}

	isEnquiryCentreFlagEnabled() {
		return posthog.isFeatureEnabled('enquiry-centre');
	}
}

export default NavbarComponent;
